import { z } from 'zod'

export const audioSettingsSchema = z.object({
  selectedSoundId: z.string(), // catalog id OR 'speak' for TTS
  volume: z.number().min(0).max(100),
  cooldownSec: z.number().min(0).max(3600),
  // Cooldown's last stop: each ticker alerts once per session (alert-ledger.ts).
  // Optional and beside cooldownSec (kept at 600) so builds that predate it
  // read a 10m cooldown instead of failing the schema.
  alertOnce: z.boolean().optional(),
  soundEnabled: z.boolean(),
  ttsEnabled: z.boolean(),
  // Alert voice (backend CLIP_VOICES key). Optional for panels persisted
  // before the picker existed; absent means the app default.
  ttsVoice: z.string().optional(),
  bufferLimit: z.number().int().min(1).max(10),
  tickerLimit: z.number().int().min(1).max(10),
})
export type AudioSettings = z.infer<typeof audioSettingsSchema>

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  selectedSoundId: 'ambient',
  volume: 60,
  cooldownSec: 60,
  soundEnabled: false,
  ttsEnabled: false,
  ttsVoice: 'aaron',
  bufferLimit: 3,
  tickerLimit: 3,
}

/**
 * Exclusive audio mode for the settings UI. Storage stays the two booleans
 * (zero migration); the dropdown derives its value and writes them mutually
 * exclusive. Legacy states resolve TTS-first: both-true, or the old
 * 'speak' sound sentinel with sound enabled, both meant "speak the alert".
 */
export type AlertAudioMode = 'off' | 'sound' | 'tts'

export function audioModeOf(s: AudioSettings): AlertAudioMode {
  if (s.ttsEnabled) return 'tts'
  if (s.soundEnabled) return s.selectedSoundId === 'speak' ? 'tts' : 'sound'
  return 'off'
}

export function withAudioMode(s: AudioSettings, mode: AlertAudioMode): AudioSettings {
  return { ...s, soundEnabled: mode === 'sound', ttsEnabled: mode === 'tts' }
}
